import { useEffect, useRef, useState } from 'react';

/**
 * Detects whether an element's content is horizontally truncated.
 *
 * Uses a ResizeObserver to re-check whenever the element's size changes
 * (e.g. on window resize or column width adjustment), and a MutationObserver
 * to re-check when its text changes, since new text in a fixed-width element
 * does not change its size.
 *
 * Returns a ref to attach to the element and a boolean indicating
 * whether its content overflows (scrollWidth > clientWidth).
 */
export const useTruncationDetection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const check = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    check();

    const resizeObserver = new ResizeObserver(check);
    resizeObserver.observe(el);

    const mutationObserver = new MutationObserver(check);
    mutationObserver.observe(el, {
      characterData: true,
      childList: true,
      subtree: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return { ref, isTruncated };
};
